import {
  Checkbox,
  IconButton,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  Typography,
  Grid,
  Button,
  Box,
  TextField,
  InputAdornment,
  LinearProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import QrCodeIcon from "@mui/icons-material/QrCode";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import GroupageShipmentRow from "./GroupageShipmentRow";
import {
  addShipmentToGroupage,
  deleteGroupage,
  getGroupage,
  getGroupages,
  removeShipmentsFromGroupage,
} from "../features/groupages/groupageSlices";
import GroupageFormDialog from "./GroupageFormDialog";
import TrackingStatusFormDialog from "./TrackingStatusFormDialog";
import { useLocation } from "react-router-dom";
import groupageService from "../features/groupages/groupageService";
import { reset } from "../features/shipments/shipmentSlice";
import TrackingStatusTimeline from "./TrackingStatusTimeline";

const GroupageShipmentList = (props) => {
  const { groupage, onSearchOpen } = props;
  const dispatch = useDispatch();
  const location = useLocation();

  const listLimit = 100;
  const shipments = groupage?.Shipments;
  const [printMenuAnchorEl, setPrintMenuAnchorEl] = useState(null);
  const printMenuOpen = Boolean(printMenuAnchorEl);
  const [selectedShipmentIds, setSelectedShipmentIds] = useState([]);
  const [shipmentQuery, setShipmentQuery] = useState("");
  const [groupageFormDialogOpen, setGroupageFormDialogOpen] = useState(false);
  const [trackingStatusFormOpen, setTrackingStatusFormOpen] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const [shipment, setShipment] = useState({});
  const query = searchParams.get("q") ?? "";

  const { isLoading, isSuccess, isError, message } = useSelector(
    (state) => state.groupages,
  );
  const { user } = useSelector((state) => state.auth);
  const handleDeleteGroupageClick = () => {
    if (window.confirm(`Delete Groupage ${groupage.description}?`)) {
      dispatch(deleteGroupage(groupage));
    }
  };

  const handleEditGroupageClick = () => {
    setGroupageFormDialogOpen(true);
  };

  const handleGroupageFormDialogClose = () => {
    setGroupageFormDialogOpen(false);
  };

  const handleUpdateStatusClick = () => {
    dispatch(reset());
    setTrackingStatusFormOpen(true);
  };

  const handleTrackingStatusFormClose = () => {
    setTrackingStatusFormOpen(false);
  };

  const handleUpdateTrackingStatus = (shipment) => {
    dispatch(getGroupage(groupage.id));
    setTrackingStatusFormOpen(false);
    setShipment(shipment);
  };

  const handleRemoveGroupageClick = () => {
    if (window.confirm("Remove selected shipments from groupage?")) {
      dispatch(
        removeShipmentsFromGroupage({
          groupage_id: groupage.id,
          shipment_ids: selectedShipmentIds,
        }),
      );
    }
  };

  const handleAllShipmentChecked = () => {
    if (
      selectedShipmentIds.length ===
      shipments?.filter((s) => !s.isClosed).length
    ) {
      setSelectedShipmentIds([]);
    } else {
      setSelectedShipmentIds(
        shipments.filter((s) => !s.isClosed).map((s) => s.id),
      );
    }
  };

  const handleShipmentSelected = (shipment) => {
    if (!selectedShipmentIds.includes(shipment.id)) {
      setSelectedShipmentIds((prevState) => [...prevState, shipment.id]);
    } else {
      setSelectedShipmentIds((prevState) =>
        prevState.filter((id) => id !== shipment.id),
      );
    }
  };

  const handleAddShipmentToGroupage = (e) => {
    e.preventDefault();
    if (!isLoading) {
      const data = {
        groupage_id: groupage.id,
        shipment_query: shipmentQuery,
      };

      dispatch(addShipmentToGroupage(data));
    }
  };

  const handleQrClick = async () => {
    const response = await groupageService.generateQr(groupage.id, user.token);

    if (response.data) {
      var file = new Blob([response.data], { type: "application/pdf" });
      var fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    }

    setPrintMenuAnchorEl(null);
  };

  const handleRefresh = () => {
    dispatch(getGroupage(groupage.id));
  };

  useEffect(() => {
    if (isSuccess && !isLoading && !isError) {
      setShipmentQuery("");
      setSelectedShipmentIds([]);
    }
  }, [isError, isSuccess, isLoading, message]);

  return (
    <div>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" gap={1} alignItems="center">
          {shipments && shipments.length > 0 && (
            <Checkbox
              sx={{ p: 0 }}
              checked={
                selectedShipmentIds.length > 0 &&
                shipments?.filter((s) => !s.isClosed).length ===
                  selectedShipmentIds.length
              }
              indeterminate={
                selectedShipmentIds.length > 0 &&
                selectedShipmentIds.length !==
                  shipments?.filter((s) => !s.isClosed).length
              }
              onChange={handleAllShipmentChecked}
            />
          )}
          <Typography>{`${
            shipments ? shipments.length : 0
          } Shipments`}</Typography>
        </Stack>
        {user && (
          <Stack direction="row" gap={1}>
            <Tooltip title="Refresh">
              <IconButton aria-label="refresh" onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="QR Code">
              <IconButton aria-label="qrcode" onClick={handleQrClick}>
                <QrCodeIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Consolidation">
              <IconButton aria-label="edit" onClick={handleEditGroupageClick}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Consolidation">
              <IconButton
                aria-label="delete"
                onClick={handleDeleteGroupageClick}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      {isLoading ? <LinearProgress /> : <hr />}

      <Stack gap={0} sx={{ maxHeight: "300px", overflow: "auto" }}>
        {shipments &&
          shipments
            .slice(0, listLimit)
            .map((shipment) => (
              <GroupageShipmentRow
                key={shipment.id}
                shipment={shipment}
                isChecked={selectedShipmentIds.includes(shipment.id)}
                handleChecked={handleShipmentSelected}
              />
            ))}
        {shipments &&
          shipments.length > listLimit &&
          `${shipments.length - listLimit} more`}
      </Stack>
      {selectedShipmentIds.length > 0 ? (
        <Grid container justifyContent={"space-between"} sx={{ my: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckIcon />}
            onClick={handleUpdateStatusClick}
          >
            UPDATE STATUS
          </Button>
          {!!user && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DeleteIcon />}
              color="error"
              sx={{ mr: 1 }}
              onClick={handleRemoveGroupageClick}
            >
              REMOVE
            </Button>
          )}
        </Grid>
      ) : (
        user && (
          <Box
            sx={{ my: 2 }}
            component="form"
            onSubmit={handleAddShipmentToGroupage}
          >
            <TextField
              size="small"
              label="Add Shipment"
              value={shipmentQuery}
              error={isError}
              autoComplete="off"
              helperText={isError ? message : ""}
              onChange={(e) => setShipmentQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">SKY-</InputAdornment>
                ),
              }}
              sx={{ width: 200 }}
            />
            <IconButton onClick={handleAddShipmentToGroupage}>
              <AddIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                onSearchOpen(groupage);
              }}
            >
              <SearchIcon />
            </IconButton>
          </Box>
        )
      )}
      <GroupageFormDialog
        groupage={groupage}
        open={groupageFormDialogOpen}
        handleClose={handleGroupageFormDialogClose}
      />
      {shipment && shipment.ShipmentStatusDrafts && (
        <TrackingStatusTimeline
          statuses={shipment.ShipmentStatusDrafts ?? []}
          shipment={shipment}
        />
      )}
      {trackingStatusFormOpen && (
        <TrackingStatusFormDialog
          shipmentIds={selectedShipmentIds}
          open={trackingStatusFormOpen}
          onClose={handleTrackingStatusFormClose}
          onUpdate={handleUpdateTrackingStatus}
          publicAccess={!user}
        />
      )}
    </div>
  );
};

export default GroupageShipmentList;
